import { dbStorage } from './indexedDBStorage'
import type {
  LedgerSafeAgentChangeSetDraft,
  LedgerSafeAgentChangeSetRecord,
  LedgerSafeAuthorityHeadRecord,
} from '../types/ledgerSafeAgentChangeSets'
import {
  LEDGER_SAFE_AGENT_CHANGE_SET_SCHEMA_VERSION,
  LEDGER_SAFE_AUTHORITY_HEAD_SCHEMA_VERSION,
} from '../types/ledgerSafeAgentChangeSets'
import type { PiMatrixAgentSnapshot } from '../types/piMatrixAgent'

export class LedgerSafeAuthorityConflictError extends Error {
  constructor(message = 'The chapter authority changed before the agent change set could be filed.') {
    super(message)
    this.name = 'LedgerSafeAuthorityConflictError'
  }
}

const assertDraftMatchesAuthority = (
  draft: LedgerSafeAgentChangeSetDraft,
  head: LedgerSafeAuthorityHeadRecord | undefined,
): void => {
  if (draft.changeSet.baseSnapshotId !== draft.snapshot.authorityRevisionId) {
    throw new LedgerSafeAuthorityConflictError('The agent change set does not target the supplied authority revision.')
  }
  if (head && (
    head.authorityRevisionId !== draft.snapshot.authorityRevisionId
    || head.contentHash !== draft.snapshot.contentHash
  )) {
    throw new LedgerSafeAuthorityConflictError()
  }
}

export const persistLedgerSafeAgentChangeSet = async (
  draft: LedgerSafeAgentChangeSetDraft,
): Promise<LedgerSafeAgentChangeSetRecord> => {
  const head = await dbStorage.get('ledger_safe_authority_heads', draft.chapterId)
  assertDraftMatchesAuthority(draft, head)

  const persistedAt = new Date().toISOString()
  const record: LedgerSafeAgentChangeSetRecord = {
    ...draft.changeSet,
    id: draft.changeSet.changeSetId,
    schemaVersion: LEDGER_SAFE_AGENT_CHANGE_SET_SCHEMA_VERSION,
    chapterId: draft.chapterId,
    bookId: draft.bookId,
    inquiryId: draft.inquiryId,
    baseAuthorityRevisionId: draft.snapshot.authorityRevisionId,
    baseContentHash: draft.snapshot.contentHash,
    persistedAt,
  }
  const authorityHead: LedgerSafeAuthorityHeadRecord = head || {
    chapterId: draft.chapterId,
    schemaVersion: LEDGER_SAFE_AUTHORITY_HEAD_SCHEMA_VERSION,
    bookId: draft.bookId,
    authorityRevisionId: draft.snapshot.authorityRevisionId,
    contentHash: draft.snapshot.contentHash,
    updatedAt: persistedAt,
  }

  await dbStorage.putManyAcrossStores({
    ledger_safe_agent_change_sets: [record],
    ledger_safe_authority_heads: [authorityHead],
  })
  return record
}

export const getLedgerSafeAgentChangeSet = (
  changeSetId: string,
): Promise<LedgerSafeAgentChangeSetRecord | undefined> =>
  dbStorage.get('ledger_safe_agent_change_sets', changeSetId)

export const listLedgerSafeAgentChangeSets = async (
  chapterId: string,
): Promise<LedgerSafeAgentChangeSetRecord[]> => {
  const records = await dbStorage.getAllByIndex('ledger_safe_agent_change_sets', 'chapterId', chapterId)
  return records.sort((left, right) => right.createdAt.localeCompare(left.createdAt))
}

export const getLedgerSafeAuthorityHead = (
  chapterId: string,
): Promise<LedgerSafeAuthorityHeadRecord | undefined> =>
  dbStorage.get('ledger_safe_authority_heads', chapterId)

/**
 * Reconcile the durable authority pointer after a non-agent Ledger edit or an
 * import. The snapshot builder only carries an existing revision forward when
 * its content hash still matches, so this cannot bless stale content.
 */
export const synchronizeLedgerSafeAuthorityHead = async ({
  chapterId,
  bookId,
  snapshot,
  expectedAuthority,
}: {
  chapterId: string
  bookId: string
  snapshot: PiMatrixAgentSnapshot
  expectedAuthority?: LedgerSafeAuthorityHeadRecord | null
}): Promise<LedgerSafeAuthorityHeadRecord> => {
  if (
    expectedAuthority
    && expectedAuthority.authorityRevisionId === snapshot.authorityRevisionId
    && expectedAuthority.contentHash === snapshot.contentHash
  ) return expectedAuthority

  const next: LedgerSafeAuthorityHeadRecord = {
    chapterId,
    bookId,
    schemaVersion: LEDGER_SAFE_AUTHORITY_HEAD_SCHEMA_VERSION,
    authorityRevisionId: snapshot.authorityRevisionId,
    contentHash: snapshot.contentHash,
    updatedAt: new Date().toISOString(),
  }
  return dbStorage.compareAndSetLedgerSafeAuthorityHead({
    expected: expectedAuthority || undefined,
    next,
  })
}
